import React, { useState } from 'react';
import { IconButton, Grid, Stack } from '@mui/material';
import { useQuery } from '@apollo/client/react';
import { GET_TERMINATION } from './reports.graphql';
import LoadingSpinner from '../components/LoadingSpinner';
import XLSX from 'xlsx';
import ExcelLogo from '../assets/microsoft-excel.svg?react';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

export function TerminationReport() {
  const [from, setFrom] = useState<moment.Moment | null>(moment().startOf('years'));
  const [to, setTo] = useState<moment.Moment | null>(null);
  const { loading, data } = useQuery<{ model: TerminationModel[] }>(GET_TERMINATION, { variables: { fromDate: from, toDate: to } });

  const columns: GridColDef<TerminationModel>[] = [
    {
      field: 'licenseUserName',
      headerName: 'Kunde',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'terminationRequestedDate',
      headerName: 'Mottatt',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'terminationReason',
      headerName: 'Årsak',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'licenseSubscriptions',
      headerName: 'Abonnementer',
      minWidth: 160,
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      valueGetter: (_, row) => row.licenseSubscriptions.join(', '),
    },
  ];

  return (
    <Stack spacing={2}>
      <Grid container spacing={3} sx={{alignItems: 'center'}}>
        <Grid>
          <DatePicker disableFuture label="Fra dato" value={from} onChange={setFrom} />
        </Grid>
        <Grid>
          <DatePicker disableFuture label="Til dato" value={to} onChange={setTo} />
        </Grid>
        <Grid>{data && <TerminationExcelExport model={data.model} />}</Grid>
      </Grid>
      {loading && <LoadingSpinner />}
      {data && (
        <DataGridPro
          autoHeight
          density="compact"
          rows={data.model}
          columns={columns}
          getRowId={(row) => row.id}
          localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
          pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 25,
                page: 0,
              },
            },
          }}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 700,
            },
          }}
        />
      )}
    </Stack>
  );
}

function TerminationExcelExport({ model }: { model: TerminationModel[] }) {
  const exportFile = () => {
    const workbook = XLSX.utils.book_new();
    const headingForPending: string[] = ['Kunde', 'Oppsigelse mottatt', 'Årsak', 'Abonnementer'];
    const inputHeading = headingForPending;
    const workBookName = 'OPPSIGELSER';
    const data = model.map((x) => [x.licenseUserName, x.terminationRequestedDate, x.terminationReason, x.licenseSubscriptions]);
    const input = [inputHeading, ...data];
    const sheet = XLSX.utils.aoa_to_sheet(input);
    XLSX.utils.book_append_sheet(workbook, sheet, workBookName);
    XLSX.writeFile(workbook, 'churn.xlsx');
  };
  return (
    <IconButton onClick={exportFile} title="Eksport til Excel" size="large">
      <ExcelLogo
        style={{
          width: 24,
          height: 24,
        }}
      />
    </IconButton>
  );
}

interface TerminationModel {
  licenseUserName: string;
  licenseSubscriptions: string[];
  terminationRequestedDate: string;
  terminationReason: string;
  id: number;
}
